<template>
  <div>
    <!-- VPC Toggle - only for compact layout -->
    <div v-if="useCompactLayout && cart.length > 0" class="d-flex align-center mb-2">
      <v-switch
        v-model="showVpcPrice"
        label="Prikaži VPC cijenu"
        hide-details
        dense
        class="mt-0 pt-0"
      ></v-switch>
      <v-spacer></v-spacer>
      <v-btn
        icon
        small
        color="primary"
        @click="$emit('open-search-modal')"
        class="mr-2"
      >
        <v-icon>mdi-magnify</v-icon>
      </v-btn>
      <span class="text-caption grey--text">Pritisnite slovo <strong>F</strong> za pretragu artikala</span>
    </div>

    <!-- Cart items list -->
    <component
      :is="useCompactLayout ? 'cart-item-compact' : 'cart-item'"
      v-for="(item, counter) in cart"
      :key="counter"
      :ref="'cartItem' + counter"
      :cart-item="item"
      :index="counter"
      :search-keyword="searchKeywords[counter]"
      @update:searchKeyword="$emit('update-search-keyword', counter, $event)"
      :selected-item="selectedItems[counter]"
      @update:selectedItem="updateSelectedItem(counter, $event)"
      @item-change="$emit('item-change', counter, $event)"
      :items="items"
      :is-loading="isLoading"
      :disabled="comboboxes[counter] && comboboxes[counter].disabled"
      :cash-register="cashRegister"
      :taxes="taxes"
      :tax-label="taxLabel"
      :currency-symbol="currencySymbol"
      :eracun="eracun"
      :tax-categories="taxCategories"
      :tax-exemption-reasons="taxExemptionReasons"
      :item-types="itemTypes"
      :validation-errors="validationErrors[counter] || {}"
      :show-vpc-price="showVpcPrice"
      @remove="$emit('remove-item', counter)"
      @update-unit="$emit('update-unit', counter, $event)"
      @update-tax="$emit('update-tax', counter, $event)"
      @update-quantity-input="$emit('update-quantity-input', counter, $event)"
      @update-quantity-blur="$emit('update-quantity-blur', counter, $event)"
      @update-price-input="$emit('update-price-input', counter, $event)"
      @update-price-blur="$emit('update-price-blur', counter, $event)"
      @update-vpc-price-input="$emit('update-vpc-price-input', counter, $event)"
      @update-vpc-price-blur="$emit('update-vpc-price-blur', counter, $event)"
      @update-discount-input="$emit('update-discount-input', counter, $event)"
      @update-discount-blur="$emit('update-discount-blur', counter, $event)"
      @update-kpdcode="$emit('update-kpdcode', counter, $event)"
      @update-tax-category="$emit('update-tax-category', counter, $event)"
      @update-tax-exemption-reason="$emit('update-tax-exemption-reason', counter, $event)"
      @update-item-type="$emit('update-item-type', counter, $event)"
    />

    <!-- Add item button and totals row -->
    <div class="d-flex mb-3 totals-row align-center">
      <div>
        <v-btn
          id="plusBtn"
          :disabled="canAddNewItem || !cashRegister"
          color="warning"
          style="font-size: 2rem"
          @click="$emit('add-item')"
        >
          +
        </v-btn>
      </div>

      <!-- Mass discount controls -->
      <div class="d-flex align-center ml-4" v-if="cart.length > 0">
        <v-text-field
          v-model="massDiscountPercent"
          label="Popust %"
          type="number"
          outlined
          dense
          hide-details
          style="max-width: 120px;"
          class="mr-2"
        ></v-text-field>
        <v-btn
          color="primary"
          @click="applyMassDiscount"
          :disabled="!massDiscountPercent || massDiscountPercent <= 0"
        >
          Dodaj na sve artikle
        </v-btn>
      </div>

      <v-spacer></v-spacer>
      <div align="right">
        <span
          ref="formatted_total"
          class="total-price"
          v-html="formattedTotal"
        ></span>
      </div>
      <div>
        <v-select
          :value="selectedCurrency"
          @input="$emit('update:selectedCurrency', $event)"
          class="curreny-selector"
          :items="currencies"
          outlined
          dense
          hide-details
        >
        </v-select>
      </div>
    </div>
  </div>
</template>

<script>
import CartItem from './CartItem.vue'
import CartItemCompact from './CartItemCompact.vue'

export default {
  name: 'CartItemsList',
  components: {
    CartItem,
    CartItemCompact
  },
  props: {
    useCompactLayout: {
      type: Boolean,
      default: false
    },
    cart: {
      type: Array,
      required: true
    },
    searchKeywords: {
      type: Array,
      required: true
    },
    selectedItems: {
      type: Array,
      required: true
    },
    items: {
      type: Array,
      required: true
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    comboboxes: {
      type: Array,
      required: true
    },
    cashRegister: {
      type: Object,
      default: null
    },
    taxes: {
      type: Array,
      required: true
    },
    taxLabel: {
      type: String,
      required: true
    },
    currencySymbol: {
      type: String,
      required: true
    },
    eracun: {
      type: Boolean,
      default: false
    },
    taxCategories: {
      type: Array,
      default: () => []
    },
    taxExemptionReasons: {
      type: Array,
      default: () => []
    },
    itemTypes: {
      type: Array,
      required: true
    },
    canAddNewItem: {
      type: Boolean,
      default: false
    },
    formattedTotal: {
      type: String,
      required: true
    },
    selectedCurrency: {
      type: String,
      required: true
    },
    currencies: {
      type: Array,
      required: true
    },
    validationErrors: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      massDiscountPercent: null,
      showVpcPrice: false
    }
  },
  methods: {
    updateSelectedItem (index, value) {
      this.$emit('update-selected-item', index, value)
    },
    focusItem (index) {
      // Metoda za fokusiranje specific cart item-a
      const cartItemRef = this.$refs['cartItem' + index]
      if (cartItemRef && cartItemRef[0]) {
        cartItemRef[0].focus()
      }
    },
    applyMassDiscount () {
      if (!this.massDiscountPercent || this.massDiscountPercent <= 0) {
        return
      }
      // Emit event to parent with discount percentage
      this.$emit('apply-mass-discount', this.massDiscountPercent)
      // Reset field
      this.massDiscountPercent = null
    }
  }
}
</script>

<style scoped>
.totals-row {
  align-items: center;
}

.total-price {
  font-size: 1.5rem;
  font-weight: bold;
  margin-right: 1rem;
}

.curreny-selector {
  max-width: 100px;
}
</style>
